/**
 * wishlist.js — Pipeline Theme Wishlist
 * Storage : localStorage key "pipeline_wishlist"  (array of product handles)
 * Wishlist page : /pages/wishlist  (rendered by sections/page-wishlist.liquid)
 */

(function () {
  'use strict';

  const WISHLIST_KEY = 'pipeline_wishlist';

  // ─── Core Store ────────────────────────────────────────────────────────────
  window.WishlistStore = {
    get() {
      try {
        return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
      } catch (e) {
        return [];
      }
    },
    save(items) {
      try {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(items));
      } catch (e) {
        console.warn('[Wishlist] localStorage not available');
      }
    },
    add(handle) {
      const items = this.get();
      if (!items.includes(handle)) {
        items.push(handle);
        this.save(items);
      }
    },
    remove(handle) {
      this.save(this.get().filter(h => h !== handle));
    },
    has(handle) {
      return this.get().includes(handle);
    },
    toggle(handle) {
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      }
      this.add(handle);
      return true;
    },
    count() {
      return this.get().length;
    }
  };

  // ─── Button State ───────────────────────────────────────────────────────────
  function setButtonState(btn, isActive) {
    const label = btn.querySelector('.wishlist-btn__label');
    btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    if (isActive) {
      btn.classList.add('wishlist-btn--active');
      if (label) label.textContent = '♥ 已加入心願單';
    } else {
      btn.classList.remove('wishlist-btn--active');
      if (label) label.textContent = '♡ 加入心願單';
    }
  }

    // ─── Count Badges (optional header icon) ──────────────────────────────────
    function updateCountBadges() {
      const count = WishlistStore.count();
      document.querySelectorAll('[data-wishlist-count]').forEach(el => {
        el.textContent    = count;
        el.style.display  = count > 0 ? '' : 'none';
      });
    }

  // ─── Init Buttons ───────────────────────────────────────────────────────────
  function initButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach(btn => {
      const handle = btn.dataset.productHandle;
      if (!handle) return;

      // Set initial state
      setButtonState(btn, WishlistStore.has(handle));

      btn.addEventListener('click', () => {
        const isActive = WishlistStore.toggle(handle);
        setButtonState(btn, isActive);
        updateCountBadges();
      });
    });
  }

  // ─── Money Formatter ────────────────────────────────────────────────────────
  function formatMoney(cents) {
    const fmt    = window.wishlist_money_format || '${{amount}}';
    const amount = (cents / 100).toFixed(2);
    return fmt
      .replace('{{amount}}',                   amount)
      .replace('{{amount_no_decimals}}',        Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }

  // ─── Image URL Helper ───────────────────────────────────────────────────────
  function sizedImageUrl(src, size) {
    if (!src) return '';
    if (src.startsWith('//')) src = 'https:' + src;
    // Replace an existing Shopify size suffix, or add one before the extension
    return src.replace(/(_\d+x\d*|_x\d+)?(\.[a-z]{2,4})(\?.*)?$/i, `_${size}x$2$3`);
  }

  // ─── HTML Escaper ───────────────────────────────────────────────────────────
  function escapeHTML(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ─── Product Card HTML ──────────────────────────────────────────────────────
  function productCardHTML(p) {
    const variant      = p.variants[0];
    const price        = formatMoney(variant.price);
    const hasDiscount  = variant.compare_at_price && variant.compare_at_price > variant.price;
    const comparePrice = hasDiscount ? formatMoney(variant.compare_at_price) : null;
    const imgSrc       = sizedImageUrl(p.featured_image || '', 500);

    return `
      <div class="wishlist-card" data-wishlist-card="${p.handle}">
        <a href="/products/${p.handle}" class="wishlist-card__image-wrap">
          ${imgSrc
            ? `<img src="${imgSrc}" alt="${escapeHTML(p.title)}" loading="lazy" class="wishlist-card__image">`
            : '<div class="wishlist-card__image-placeholder"></div>'
          }
          ${!variant.available ? '<span class="wishlist-card__badge">售完</span>' : ''}
        </a>
        <div class="wishlist-card__body">
          <a href="/products/${p.handle}" class="wishlist-card__title">${escapeHTML(p.title)}</a>
          <div class="wishlist-card__price">
            <span class="wishlist-card__price-current${hasDiscount ? ' wishlist-card__price-sale' : ''}">${price}</span>
            ${comparePrice ? `<s class="wishlist-card__price-compare">${comparePrice}</s>` : ''}
          </div>
          <div class="wishlist-card__actions">
            <a href="/products/${p.handle}" class="btn--outline wishlist-card__btn-view">查看商品</a>
            <button
              type="button"
              class="wishlist-card__btn-remove"
              data-remove-handle="${p.handle}"
              aria-label="從心願單移除 ${escapeHTML(p.title)}"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              移除
            </button>
          </div>
        </div>
      </div>`;
  }

  // ─── Empty State HTML ───────────────────────────────────────────────────────
  function emptyStateHTML() {
    return `
      <div class="wishlist-empty">
        <svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
        <p>你的心願單還是空的</p>
        <a href="/collections/all" class="btn--outline wishlist-empty__cta">繼續購物</a>
      </div>`;
  }

  // ─── Wishlist Page Loader ───────────────────────────────────────────────────
  async function loadWishlistPage() {
    const container = document.getElementById('wishlist-items');
    if (!container) return;

    const handles = WishlistStore.get();

    if (handles.length === 0) {
      container.innerHTML = emptyStateHTML();
      return;
    }

    container.innerHTML = '<p class="wishlist-loading">載入中⋯</p>';

    // Fetch all products in parallel
    const results = await Promise.all(
      handles.map(h =>
        fetch(`/products/${h}.js`)
          .then(r => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );

    // Clean up handles that no longer exist
    results.forEach((product, i) => {
      if (!product) WishlistStore.remove(handles[i]);
    });

    const products = results.filter(Boolean);

    if (products.length === 0) {
      container.innerHTML = emptyStateHTML();
      return;
    }

    container.innerHTML = `
      <div class="wishlist-grid">
        ${products.map(p => productCardHTML(p)).join('')}
      </div>`;

    updateCountBadges();

    // Attach remove-button listeners
    container.querySelectorAll('[data-remove-handle]').forEach(btn => {
      btn.addEventListener('click', () => {
        const handle = btn.dataset.removeHandle;
        WishlistStore.remove(handle);
        updateCountBadges();

        const card = container.querySelector(`[data-wishlist-card="${handle}"]`);
        if (card) {
          card.style.transition = 'opacity 0.25s ease';
          card.style.opacity    = '0';
          setTimeout(() => {
            card.remove();
            if (!container.querySelector('[data-wishlist-card]')) {
              container.innerHTML = emptyStateHTML();
            }
          }, 260);
        }
      });
    });
  }

  // ─── Boot ───────────────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', () => {
    initButtons();
    updateCountBadges();
    loadWishlistPage();   // no-op if not on wishlist page
  });

})();
